import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, ChevronDown, ChevronRight, Clock, XCircle, Zap } from 'lucide-react';
import { useOrgStore } from '../store/useOrgStore';
import { FlowsTable } from '../components/tables/FlowsTable';
import { EmptyState } from '../components/ui/EmptyState';
import { RiskBadge } from '../components/ui/RiskBadge';
import { CollapsibleSection } from '../components/ui/CollapsibleSection';
import { AutomationByObjectTab } from '../components/automation/AutomationByObjectTab';
import type { FlowErrorGroup, FlowSummary } from '../types/org.types';

type Filter = 'all' | 'active' | 'inactive';

const PROCESS_BUILDER_TYPES = new Set(['Workflow', 'CustomEvent']);
const isWorkflowRule = (f: FlowSummary) => f.processType === 'WorkflowRule';
const isProcessBuilder = (f: FlowSummary) => PROCESS_BUILDER_TYPES.has(f.processType);

export function FlowsPage() {
  const flows = useOrgStore((s) => s.audit.flows);
  const [filter, setFilter] = useState<Filter>('all');
  // Deep link: ?tab=by-object&object=<ApiName> opens the tab on one object.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'by-object' ? 'by-object' : 'all-automation';
  const openObject = searchParams.get('object');
  const selectTab = (next: 'all-automation' | 'by-object') => {
    const params = new URLSearchParams(searchParams);
    if (next === 'by-object') params.set('tab', 'by-object');
    else {
      params.delete('tab');
      params.delete('object');
    }
    setSearchParams(params, { replace: true });
  };

  const { workflowRules, processBuilders, modernFlows } = useMemo(() => {
    if (!flows) return { workflowRules: [], processBuilders: [], modernFlows: [] };
    return {
      workflowRules: flows.flows.filter(isWorkflowRule),
      processBuilders: flows.flows.filter(isProcessBuilder),
      modernFlows: flows.flows.filter((f) => !isWorkflowRule(f) && !isProcessBuilder(f)),
    };
  }, [flows]);

  const filteredModern = useMemo(() => {
    if (filter === 'active') return modernFlows.filter((f) => f.status === 'Active');
    if (filter === 'inactive') return modernFlows.filter((f) => f.status !== 'Active');
    return modernFlows;
  }, [modernFlows, filter]);

  if (!flows) return <EmptyState title="No flow data. Reconnect to scan again." />;

  const t = flows.totals;
  const tabs = [
    { key: 'all' as const, label: 'All', count: modernFlows.length },
    { key: 'active' as const, label: 'Active', count: modernFlows.filter((f) => f.status === 'Active').length },
    { key: 'inactive' as const, label: 'Inactive', count: modernFlows.filter((f) => f.status !== 'Active').length },
  ];

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-deliver mb-1">Deliver</div>
        <h1 className="text-3xl font-semibold text-crux-light tracking-tight">Flow & Automation Audit</h1>
        <p className="text-sm text-neutral-500 mt-1">
          {modernFlows.length} modern flow{modernFlows.length === 1 ? '' : 's'} · {processBuilders.length} Process
          Builder{processBuilders.length === 1 ? '' : 's'} · {workflowRules.length} Workflow Rule
          {workflowRules.length === 1 ? '' : 's'} · {t.apexTriggersActive} Apex triggers ·{' '}
          {t.apexClassesActive} Apex classes
        </p>
      </div>

      <div className="flex border-b border-border-strong">
        {([
          { key: 'all-automation' as const, label: 'All automation' },
          { key: 'by-object' as const, label: 'By object' },
        ]).map((t2) => (
          <button
            key={t2.key}
            type="button"
            onClick={() => selectTab(t2.key)}
            className={`text-sm font-semibold px-4 py-2.5 border border-b-0 -mb-px ${
              tab === t2.key
                ? 'bg-surface-2 border-border-strong text-crux-light'
                : 'border-transparent text-neutral-500 hover:text-crux-light'
            }`}
          >
            {t2.label}
          </button>
        ))}
      </div>

      {tab === 'by-object' ? (
        <AutomationByObjectTab data={flows.byObject} openObject={openObject} />
      ) : (
      <div className="space-y-8">
      {/* ── Flow run errors (grouped by failing automation) ─────────────── */}
      <FlowErrorsSection groups={flows.errors?.groups ?? []} total={flows.errors?.totalErrors ?? 0} />

      {/* ── Legacy automation: Workflow Rules ───────────────────────────── */}
      {workflowRules.length > 0 && (
        <LegacySection
          title="Workflow Rules"
          subtitle={`Salesforce retires Workflow Rules in Winter ’27. ${workflowRules.length} active rule${workflowRules.length === 1 ? '' : 's'} must migrate to Flow before then.`}
          tone="critical"
          icon={<AlertTriangle size={16} />}
          items={workflowRules}
          riskLabel="Retire"
          subjectLabel="Triggers on"
          countLabel="rules"
        />
      )}

      {/* ── Legacy automation: Process Builders ─────────────────────────── */}
      {processBuilders.length > 0 && (
        <LegacySection
          title="Process Builders"
          subtitle={`${processBuilders.length} Process Builder process${processBuilders.length === 1 ? '' : 'es'} detected. Salesforce stopped accepting new ones in Spring ’23. Migrate to Flow.`}
          tone="warning"
          icon={<Clock size={16} />}
          items={processBuilders}
          riskLabel="Migrate"
          subjectLabel="Triggers on"
          countLabel="processes"
        />
      )}

      {workflowRules.length === 0 && processBuilders.length === 0 && (
        <div className="flex items-start gap-3 bg-grow/10 border border-grow/30 rounded-md p-3 text-sm text-grow">
          <Zap size={16} className="mt-0.5 shrink-0" />
          <span>No legacy automation detected. Workflow Rules and Process Builder are both clear.</span>
        </div>
      )}

      {/* ── Modern flows ────────────────────────────────────────────────── */}
      <CollapsibleSection
        title="Modern Flows"
        meta={`${filteredModern.length} shown of ${modernFlows.length}`}
        defaultOpen={modernFlows.length <= 20}
      >
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setFilter(tab.key)}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                filter === tab.key
                  ? 'bg-deliver/15 border-deliver/40 text-deliver'
                  : 'bg-surface-2 border-border text-neutral-400 hover:text-crux-light'
              }`}
            >
              {tab.label} <span className="text-neutral-500">({tab.count})</span>
            </button>
          ))}
        </div>
        <FlowsTable rows={filteredModern} />
      </CollapsibleSection>
      </div>
      )}
    </div>
  );
}

function LegacySection({
  title,
  subtitle,
  tone,
  icon,
  items,
  riskLabel,
  subjectLabel,
  countLabel,
}: {
  title: string;
  subtitle: string;
  tone: 'critical' | 'warning';
  icon: React.ReactNode;
  items: FlowSummary[];
  riskLabel: string;
  subjectLabel: string;
  countLabel: string;
}) {
  const accent =
    tone === 'critical'
      ? 'border-sunset/40 bg-sunset/5'
      : 'border-sunrise/40 bg-sunrise/5';
  const headingColor = tone === 'critical' ? 'text-sunset' : 'text-sunrise';
  const badgeTone = tone === 'critical' ? 'critical' : 'high';

  return (
    <section className={`border rounded-lg ${accent} overflow-hidden`}>
      <header className="flex items-start gap-3 px-5 py-4 border-b border-border/60">
        <span className={`mt-0.5 ${headingColor}`}>{icon}</span>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className={`text-lg font-semibold ${headingColor}`}>{title}</h2>
            <span className="px-2 py-0.5 text-xs rounded-full bg-surface-3 border border-border text-neutral-300 tabular-nums">
              {items.length} {countLabel}
            </span>
          </div>
          <p className="text-sm text-neutral-400 mt-1">{subtitle}</p>
        </div>
      </header>
      <table className="w-full text-sm">
        <thead className="bg-surface-2/50">
          <tr className="text-[11px] uppercase tracking-widest text-neutral-500">
            <th className="text-left px-5 py-2.5 font-medium">Name</th>
            <th className="text-left px-5 py-2.5 font-medium">{subjectLabel}</th>
            <th className="text-left px-5 py-2.5 font-medium">Status</th>
            <th className="text-left px-5 py-2.5 font-medium">Action</th>
          </tr>
        </thead>
        <tbody>
          {items.map((f, i) => (
            <tr
              key={f.id}
              className={i % 2 === 0 ? 'bg-surface-1/40' : 'bg-transparent'}
            >
              <td className="px-5 py-2.5">
                <div className="text-crux-light">{f.label}</div>
                <div className="text-[11px] text-neutral-600 font-mono">{f.apiName}</div>
              </td>
              <td className="px-5 py-2.5 text-neutral-400">
                {f.triggerObject ?? 'No object'}
              </td>
              <td className="px-5 py-2.5 text-neutral-300">{f.status}</td>
              <td className="px-5 py-2.5">
                <RiskBadge tone={badgeTone}>{riskLabel}</RiskBadge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function FlowErrorsSection({ groups, total }: { groups: FlowErrorGroup[]; total: number }) {
  if (total === 0) {
    return (
      <div className="flex items-start gap-3 bg-grow/10 border border-grow/30 rounded-md p-3 text-sm text-grow">
        <Zap size={16} className="mt-0.5 shrink-0" />
        <span>No flow run errors detected. Every active automation is succeeding.</span>
      </div>
    );
  }
  return (
    <section className="border border-sunset/40 bg-sunset/5 rounded-lg overflow-hidden">
      <header className="flex items-start gap-3 px-5 py-4 border-b border-border/60">
        <XCircle size={16} className="mt-0.5 text-sunset" />
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-sunset">Flow Run Errors</h2>
            <span className="px-2 py-0.5 text-xs rounded-full bg-surface-3 border border-border text-neutral-300 tabular-nums">
              {total} error{total === 1 ? '' : 's'} across {groups.length} flow
              {groups.length === 1 ? '' : 's'}
            </span>
          </div>
          <p className="text-sm text-neutral-400 mt-1">
            Grouped by the failing automation. Expand a row to see the most recent failed
            interviews and where in the flow they got stuck.
          </p>
        </div>
      </header>
      <ul className="divide-y divide-border/60">
        {groups.map((g) => (
          <FlowErrorGroupRow key={g.flowName} group={g} />
        ))}
      </ul>
    </section>
  );
}

function FlowErrorGroupRow({ group }: { group: FlowErrorGroup }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="px-5 py-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 text-left"
      >
        <span className="text-neutral-500">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-crux-light truncate">
            {group.flowLabel ?? group.flowName}
          </div>
          {group.flowLabel ? (
            <div className="text-[11px] text-neutral-600 font-mono truncate">{group.flowName}</div>
          ) : null}
        </div>
        <RiskBadge tone={group.count >= 10 ? 'critical' : group.count >= 3 ? 'high' : 'medium'}>
          {group.count} error{group.count === 1 ? '' : 's'}
        </RiskBadge>
        <span className="text-xs text-neutral-500 hidden sm:inline">
          last · {new Date(group.lastErrorAt).toLocaleDateString()}
        </span>
      </button>
      {open && group.samples.length > 0 && (
        <div className="mt-3 ml-7 border-l border-border pl-4 space-y-2">
          {group.samples.map((s) => (
            <div
              key={s.interviewId}
              className="text-xs grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-x-3 gap-y-0.5"
            >
              <div className="text-crux-light truncate">
                {s.interviewLabel ?? '(no label)'}
                {s.currentElement ? (
                  <span className="text-neutral-500"> · stuck at </span>
                ) : null}
                {s.currentElement ? (
                  <span className="font-mono text-sunset">{s.currentElement}</span>
                ) : null}
              </div>
              <div className="text-neutral-500 text-right tabular-nums">
                {new Date(s.createdAt).toLocaleString()}
                {s.createdBy ? <span className="ml-2">· {s.createdBy}</span> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </li>
  );
}

